import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo } from "react";
import { App, Form } from "antd";
import SegmentPanel from "../SegmentPanel";
import TranscriptionPanel from "../TranscriptionPanel";
import RecordingPanel from "../RecordingPanel";
import { blankFormValues, formValuesFromSegment, getSegmentPanelTitleId, safelyRestoreSegmentSettings, storeSegmentSettings } from "../segment-record";
import { createSegment, startSession } from "api/segment";
import TranslationPanel from "../TranslationPanel";
import { useI18n } from "components/I18nProvider";
import { useEventAdminState } from "components/EventStateProvider";
import { findSegment, getMicAudioStatus, getRecordTabUiState, pickNextSegmentIdInOrder, pickPreviousSegmentIdInOrder, } from 'lib/event-state-merge';
import { useFinishSegment } from 'lib/use-finish-segment';
import { suggestDefaultSegmentTitle } from 'lib/segment-title';
import PresentersPanel from '../PresentersPanel';
import styles from './style.module.scss';
const isServerUnavailableError = (error) => (error instanceof TypeError && error.message === 'Failed to fetch')
    || error instanceof SyntaxError;
const SegmentRecordSection = ({ eventId, config, testRecordings, }) => {
    const { intl } = useI18n();
    const { message } = App.useApp();
    const { eventSnapshot, selectedSegmentId, setSelectedSegmentId, transportOnline, actionWaiting, setActionWaiting, } = useEventAdminState();
    const [form] = Form.useForm();
    const initialValues = safelyRestoreSegmentSettings(config);
    const selectedSegment = findSegment(eventSnapshot, selectedSegmentId);
    const segmentFormMode = selectedSegmentId === null ? 'new'
        : selectedSegment === null ? 'missing'
            : 'selected';
    const micStatus = useMemo(() => getMicAudioStatus({
        eventSnapshot,
        segmentId: selectedSegmentId,
    }), [eventSnapshot, selectedSegmentId]);
    const { finishSegment } = useFinishSegment(eventId);
    const ui = getRecordTabUiState({
        eventSnapshot,
        selectedSegmentId,
        transportOnline,
        actionWaiting,
    });
    const segmentPanelTitleId = getSegmentPanelTitleId(selectedSegmentId, selectedSegment?.status);
    useEffect(() => {
        const base = safelyRestoreSegmentSettings(config);
        if (segmentFormMode === 'new') {
            form.setFieldsValue({
                ...base,
                title: suggestDefaultSegmentTitle(eventSnapshot?.segments ?? []),
            });
            return;
        }
        if (segmentFormMode === 'missing') {
            form.setFieldsValue({
                ...blankFormValues(config),
                title: intl.formatMessage({ id: 'selectedSegmentMissing' }),
            });
            return;
        }
        form.setFieldsValue(formValuesFromSegment(selectedSegment, base));
    }, [segmentFormMode, selectedSegmentId, selectedSegment, eventSnapshot?.segments, config, intl, form]);
    const handleSubmit = async (values) => {
        if (actionWaiting) {
            return;
        }
        if (!ui.showStart) {
            return;
        }
        setActionWaiting(true);
        try {
            const existingCreatedSegment = selectedSegment !== null && selectedSegment.status === 'created'
                ? selectedSegment
                : null;
            const segmentId = existingCreatedSegment?.id ?? (await createSegment(eventId, {
                segmentTitle: values.title,
                audioLanguage: values.inputLanguage,
                translationInit: values.translationEnabled
                    && values.outputLanguages.length > 0
                    && config.translationProfiles.length > 0
                    && values.translationProfileId
                    ? {
                        languages: values.outputLanguages.filter((lang) => lang !== values.inputLanguage),
                        profileId: values.translationProfileId,
                    }
                    : null,
                engineId: values.engineId,
                enableSpeechSegmentation: values.enableSpeechSegmentation,
                enableTTS: values.enableTTS,
            })).id;
            setSelectedSegmentId(segmentId);
            void startSession(eventId, segmentId).catch((error) => {
                setActionWaiting(false);
                void message.error(intl.formatMessage({
                    id: isServerUnavailableError(error) ? 'segmentStartFailedNetwork' : 'segmentStartFailed',
                }));
            });
        }
        catch (error) {
            setActionWaiting(false);
            void message.error(intl.formatMessage({
                id: isServerUnavailableError(error) ? 'segmentStartFailedNetwork' : 'segmentStartFailed',
            }));
        }
    };
    const handleFormValuesChange = (newValues) => {
        storeSegmentSettings(newValues);
    };
    const handlePreviousSegment = () => {
        setSelectedSegmentId(pickPreviousSegmentIdInOrder(eventSnapshot, selectedSegmentId));
    };
    const handleNextSegment = () => {
        setSelectedSegmentId(pickNextSegmentIdInOrder(eventSnapshot, selectedSegmentId));
    };
    const showSegmentNavigation = (eventSnapshot?.segments.length ?? 0) > 0;
    return (_jsxs(_Fragment, { children: [_jsxs(Form, { className: styles.form, form: form, labelWrap: true, layout: 'vertical', initialValues: initialValues, onFinish: handleSubmit, onValuesChange: (_, values) => handleFormValuesChange(values), children: [_jsx(SegmentPanel, { form: form, engines: config.s2tEngines, disabled: ui.formDisabled, panelTitleId: segmentPanelTitleId, showSegmentNavigation: showSegmentNavigation, onPreviousSegment: handlePreviousSegment, onNextSegment: handleNextSegment }), _jsx(TranscriptionPanel, { eventId: eventId, eventSnapshot: eventSnapshot, replacements: eventSnapshot?.replacements ?? [], createBeeyProject: eventSnapshot?.createBeeyProject ?? false, selectedSegmentId: selectedSegmentId, selectedBeeyProjectId: selectedSegment?.beeyProjectId, selectedBeeyProjectState: selectedSegment?.beeyProjectState, transportOnline: transportOnline, userlexDisabled: ui.formDisabled }), _jsx(TranslationPanel, { form: form, outputLanguages: config.outputLanguages, translationProfiles: config.translationProfiles, disabled: ui.formDisabled }), _jsx(RecordingPanel, { selectedSegmentId: selectedSegmentId, micStatus: micStatus, translationProfiles: config.translationProfiles, webSocketViewerChannelCapacity: config.webSocketViewerChannelCapacity, stopping: actionWaiting && ui.showFinish, starting: actionWaiting && ui.showStart, showStart: ui.showStart, showFinish: ui.showFinish, showNextSegment: ui.showNextSegment, onNextSegment: handleNextSegment, onFinishSegment: () => {
                            if (selectedSegmentId !== null) {
                                void finishSegment(selectedSegmentId);
                            }
                        }, startDisabled: ui.startDisabled, finishDisabled: ui.finishDisabled })] }), _jsx(PresentersPanel, { eventId: eventId, eventSnapshot: eventSnapshot, publicEventOrigin: config.publicEventOrigin })] }));
};
export default SegmentRecordSection;
